<script lang="ts">
/** One step in the rail. */
export interface GaiaStepRailItem {
  key: string;
  label: string;
  /** Numbered circle before the label (the Prepare-signature wizard). */
  number?: number;
  /** Shown while this step is the active one. */
  description?: string;
  /** Mono count shown while the step is not active, e.g. the item count. */
  count?: string | number;
  /** Already completed — the numbered circle fills in and shows a check. */
  done?: boolean;
  /** Not reachable yet — renders a lock and stays unclickable. */
  locked?: boolean;
}
</script>

<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFonts, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  items: {
    type: Array as PropType<GaiaStepRailItem[]>,
    required: true,
  },
  /** Key of the active step. */
  active: {
    type: String,
    required: true,
  },
});

const emit = defineEmits<{ select: [key: string] }>();

function pick(item: GaiaStepRailItem): void {
  if (!item.locked) emit('select', item.key);
}

const ink = gaiaNeutral.ink;
const idle = gaiaNeutral.textTertiary;
const activeBg = gaiaNeutral.surfaceTint;
const hover = gaiaNeutral.surfaceHover;
const lock = gaiaNeutral.textMuted;
const monoFont = gaiaFonts.mono;
const numBorder = gaiaNeutral.border;
const onInk = gaiaNeutral.surface;
</script>

<template>
  <!-- Wizard step rail (Playbooks "Create playbook"): a step list with an ink
       bar on the active step, its description, and per-step counts or locks.
       naive's n-steps is a linear progress indicator — it can't be a
       navigable rail with counts, so this is a bespoke list. -->
  <nav class="gaia-step-rail">
    <button
      v-for="item in props.items"
      :key="item.key"
      type="button"
      class="step"
      :class="{ active: item.key === props.active, done: item.done, locked: item.locked }"
      :disabled="item.locked"
      @click="pick(item)"
    >
      <span class="bar" />
      <!-- Keeps .num for its box and colour: they flip on .step.active,
           .step.done and .step.locked, which no GaiaText prop can express. A
           finished step swaps the numeral for a check. -->
      <gaia-text v-if="item.number !== undefined" class="num" size="nano" weight="bold">
        <svg
          v-if="item.done"
          width="9"
          height="9"
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M1.6 5.2 3.9 7.5 8.4 2.8" />
        </svg>
        <template v-else>{{ item.number }}</template>
      </gaia-text>
      <n-flex vertical :size="3" :style="{ minWidth: 0, flex: 1 }">
        <n-flex align="baseline" :size="8" :wrap="false">
          <!-- Weight and colour flip with the active step, so they ride the
               props rather than a selector. -->
          <gaia-text
            truncate
            size="dense"
            :weight="item.key === props.active ? 'bold' : 'medium'"
            :tone="item.key === props.active ? 'ink' : 'tertiary'"
          >
            {{ item.label }}
          </gaia-text>
          <svg
            v-if="item.locked"
            width="11"
            height="11"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linejoin="round"
            :style="{ marginLeft: 'auto', flex: 'none', color: lock }"
          >
            <rect x="3.5" y="7" width="9" height="6.5" rx="1.5" />
            <path d="M5.75 7V5.25a2.25 2.25 0 0 1 4.5 0V7" />
          </svg>
          <gaia-text
            v-else-if="item.count !== undefined"
            size="meta"
            tone="faint"
            :style="{ marginLeft: 'auto', flex: 'none', fontFamily: monoFont }"
          >
            {{ item.count }}
          </gaia-text>
        </n-flex>
        <gaia-text
          v-if="item.description && item.key === props.active"
          size="meta"
          tone="subtle"
          :style="{ lineHeight: 1.5, textWrap: 'pretty' }"
        >
          {{ item.description }}
        </gaia-text>
      </n-flex>
    </button>
  </nav>
</template>

<style scoped>
.gaia-step-rail {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.step {
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 9px 10px 9px 8px;
  border: none;
  border-radius: 9px;
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.step:hover:not(.locked) {
  background: v-bind(hover);
}

.step.active {
  background: v-bind(activeBg);
}

.step.locked {
  cursor: default;
}

.bar {
  width: 2px;
  flex: none;
  border-radius: 2px;
  background: transparent;
}

.step.active .bar {
  background: v-bind(ink);
}

/* The numbered circle: a fixed 19px box, ink-filled on the active step and a
   hairline otherwise. */
.num {
  width: 19px;
  height: 19px;
  margin-top: 1px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid v-bind(numBorder);
  border-radius: 50%;
  color: v-bind(idle);
}

.step.active .num,
.step.done .num {
  border-color: v-bind(ink);
  background: v-bind(ink);
  color: v-bind(onInk);
}

.step.locked .num {
  color: v-bind(lock);
}
</style>
